import {
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonPage,
  IonToolbar,
} from '@ionic/react';
import React, {
  useMemo,
  useRef,
  useEffect,
  useCallback,
  useState,
} from 'react';
import ChatTitle, {
  ChatTitleSkeleton,
} from '../../../../../components/Dashboard/chat/ChatTitle';
import { images, IonicButton } from 'ionic-ui';
import ChatMessageArea from '../../../../../components/Dashboard/chat/ChatMessageArea';
import ChatInput from '../../../../../components/Dashboard/chat/ChatInput';
import { chevronBack, chevronDownOutline } from 'ionicons/icons';
import useConversationById from '../../../../../services/messaging/hooks/useConversationById';
import { useParams } from 'react-router';
import EmptyPage from '../../../../../components/ui/EmptyPage';
import ErrorBanner from '../../../../../components/ui/ErrorBanner';
import useDirectMessaging from '../../../../../services/messaging/hooks/useDmMessaging';
import { IsTypingBubble } from '../../../../../components/Dashboard/chat/IsTypingBubble';
import { mergeSortedMessages } from '../../../../../utils/helperFunction/mergeSortedMessages';

export default function ChatRoom({ onBack }: { onBack: () => void }) {
  // GET CURRENT CONVERSATION ID
  const { id } = useParams<{ id: string }>();
  const conversation_id = Number(id);
  const ionContentRef = useRef<HTMLIonContentElement | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const bottomAreaId = 'BOTTOM_AREA';
  const scrollElRef = useRef<HTMLElement | null>(null);

  const {
    connectedToCon,
    handleUserTyping,
    isTyping,
    sendTextMessage,
    queuedMsg,
    newMessages,
    resendingTextMessage,
    markAsRead: flushUnreadMessages,
    participantsOnlineStatus,
  } = useDirectMessaging(conversation_id);

  const {
    detailQuery,
    messagesQuery: messagesHistoryQuery,
    userRoleId,
    recipient,
  } = useConversationById({ id: conversation_id, isConnected: connectedToCon });
  const [showScrollerButton, setShowScrollerButton] = useState<boolean>(false);

  const handleRefetch = () => {
    detailQuery.refetch();
    messagesHistoryQuery.refetch();
  };

  const handleInfiniteScroll = async (event: any) => {
    try {
      if (
        messagesHistoryQuery.hasNextPage &&
        !messagesHistoryQuery.isFetching
      ) {
        await messagesHistoryQuery.fetchNextPage();
      }
    } finally {
      event.target.complete();
    }
  };

  const handleScrollToBottom = () => {
    ionContentRef.current?.scrollToBottom(300);
    // bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  //================ messages ====================
  // ✅ Only recomputes on pagination — not on every new message
  const stableHistory = useMemo(() => {
    return messagesHistoryQuery.data?.pages ?? [];
  }, [messagesHistoryQuery.data?.pages]);

  // ✅ Only recomputes on socket events / typing
  const recentMessages = useMemo(() => {
    return mergeSortedMessages(newMessages, queuedMsg);
  }, [newMessages, queuedMsg]);

  // ✅ Final combine: cheap because history is pre-flattened and stable
  const messages = useMemo(() => {
    if (recentMessages.length === 0) return stableHistory;
    return mergeSortedMessages(stableHistory, recentMessages);
  }, [stableHistory, recentMessages]);

  //================ ISTYPING BUBBLE AUTO SCROLL ===============
  useEffect(() => {
    let mounted = true;

    const checkScroll = async () => {
      const content = ionContentRef.current;
      if (!content || !mounted) return;
      scrollElRef.current = await content.getScrollElement();
      try {
        if (!mounted || !scrollElRef.current) return;
        const distanceFromBottom =
          scrollElRef?.current?.scrollHeight -
          scrollElRef?.current?.scrollTop -
          scrollElRef?.current?.clientHeight;
        if (distanceFromBottom <= 150) {
          handleScrollToBottom();
        }
      } catch (err) {
        console.log(err);
      }
    };

    // Wait for paint before measuring
    const raf = requestAnimationFrame(checkScroll);

    return () => {
      mounted = false;
      cancelAnimationFrame(raf);
    };
  }, [messages?.length, isTyping?.recipient]);

  //===========================================================
  const handleMassageSending = (text: string) => {
    sendTextMessage(text);
  };
  //===========================================================

  // ======= IntersectionObserver rooted to IonContent scroll element =====
  const observerRef = useRef<IntersectionObserver | null>(null);
  const pendingNodesRef = useRef<HTMLElement[]>([]);
  const unreadMessagesRef = useRef<Set<number>>(new Set());

  const observeNode = useCallback((node: HTMLElement | null) => {
    if (node === null) {
      return;
    }

    if (observerRef.current) {
      observerRef.current.observe(node);
    } else {
      pendingNodesRef.current.push(node);
    }
  }, []);

  //=========================== HANDLE UNREAD MESSAGES FLUSH =========================
  const scrollEndTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const SCROLL_END_DELAY = 3000;
  const onScroll = useCallback(() => {
    if (scrollEndTimer.current) {
      clearTimeout(scrollEndTimer.current);
    }
    scrollEndTimer.current = setTimeout(() => {
      flushUnreadMessages(Array.from(unreadMessagesRef.current.values()));
      scrollEndTimer.current = null;
    }, SCROLL_END_DELAY);
  }, [flushUnreadMessages]);

  //===============================================================================================
  useEffect(() => {
    let cancelled = false;
    let rootEl: HTMLElement;

    const setup = async () => {
      if (!ionContentRef.current) return;
      try {
        rootEl = await ionContentRef.current.getScrollElement();
        if (!rootEl || cancelled) return;

        rootEl.addEventListener('scroll', onScroll);

        // eslint-disable-next-line
        const callback: IntersectionObserverCallback = (entries) => {
          entries.forEach((entry) => {
            /// ROLE FOR BOTTOM AREA OF CHAT
            const storedId = (entry.target as HTMLElement).dataset.id;

            if (storedId && storedId == bottomAreaId) {
              setShowScrollerButton(!entry.isIntersecting);
              return;
            }

            if (!entry.isIntersecting) return;
            const data_id = (entry.target as HTMLElement).dataset
              .unreadMessageId;
            const id = Number(data_id);
            if (!id || isNaN(id)) return;
            unreadMessagesRef.current.add(id);
            // for now we only collect ids; batching/flush handled elsewhere
          });
        };

        observerRef.current = new IntersectionObserver(callback, {
          root: rootEl,
          threshold: 0.5,
        });

        if (bottomRef.current) observerRef.current.observe(bottomRef.current);

        // observe any pending nodes
        pendingNodesRef.current.forEach((n) => observerRef.current?.observe(n));
        pendingNodesRef.current = [];
      } catch {
        // ignore
      }
    };

    setup();

    return () => {
      cancelled = true;
      rootEl?.removeEventListener('scroll', onScroll);
      observerRef.current?.disconnect();
      observerRef.current = null;
      pendingNodesRef.current = [];
      if (scrollEndTimer.current) clearTimeout(scrollEndTimer.current);
    };
  }, []);

  // =================================================================================

  // useEffect(() => {
  //   if (!ionContentRef.current) return;
  //   let scrollEl: HTMLElement;
  //   const setup = async () => {
  //     scrollEl = await ionContentRef.current!.getScrollElement();
  //     scrollEl.addEventListener('scroll', onScroll);
  //   };

  //   setup();
  //   return () => {
  //     if (scrollEndTimer.current) clearTimeout(scrollEndTimer.current);
  //     if (scrollEl) scrollEl.removeEventListener('scroll', onScroll);
  //   };
  // }, []);

  //Always Scroll to bottom when queue message increases
  useEffect(() => {
    if (messagesHistoryQuery.isLoading) return;
    const raf = requestAnimationFrame(() => handleScrollToBottom());
    return () => cancelAnimationFrame(raf);
  }, [queuedMsg, messagesHistoryQuery.isLoading]);

  if (isNaN(conversation_id))
    return (
      <EmptyPage message="Conversation not found" className="h-full w-full" />
    );

  return (
    <IonPage>
      <IonHeader className="pb-1 shadow-none">
        <IonToolbar
          style={{
            '--background': 'transparent',
            '--border-width': '0',
            '--ion-background-color': 'transparent',
            backdropFilter: 'none',
          }}
        >
          <div className="flex items-center border-b border-border shadow-sm">
            <IonicButton
              onClick={onBack}
              className="sm:hidden p-1 bg-transparent hover:bg-transparent button-with-icon "
            >
              <IonIcon
                className="size-6 medium-emphasis-text"
                icon={chevronBack}
              />
            </IonicButton>
            {detailQuery.isFetching || !recipient ? (
              <ChatTitleSkeleton />
            ) : (
              <ChatTitle
                lastSeen={participantsOnlineStatus.recipient.lastSeen}
                isOnline={participantsOnlineStatus.recipient.isOnline}
                fullName={recipient?.profile?.full_name || "Recipient's name"}
                profilePicture={
                  recipient?.profile?.profile_image_url || images.placeholder
                }
              />
            )}
          </div>
        </IonToolbar>
      </IonHeader>
      <IonContent ref={ionContentRef} className="relative">
        {/* LOADS OLDER MESSAGES */}
        {messagesHistoryQuery.isSuccess && messagesHistoryQuery.hasNextPage && (
          <IonInfiniteScroll
            position="top"
            threshold="10px"
            onIonInfinite={handleInfiniteScroll}
          >
            <IonInfiniteScrollContent loadingText="Loading..." />
          </IonInfiniteScroll>
        )}
        {detailQuery.isError ? (
          <ErrorBanner
            message={detailQuery.error?.message || 'Something went wrong'}
            className="h-full w-full"
          />
        ) : (
          <>
            {!messagesHistoryQuery.isError && !detailQuery.isError ? (
              <ChatMessageArea
                isFetching={
                  messagesHistoryQuery.isLoading ||
                  messagesHistoryQuery.isPending
                }
                userRoleId={userRoleId}
                messages={messages}
                onResend={resendingTextMessage}
                observeNode={observeNode}
                scrollElRef={scrollElRef}
              />
            ) : (
              <div className="h-full">
                <ErrorBanner
                  message={
                    messagesHistoryQuery.error?.message ||
                    'Something went wrong'
                  }
                  className="h-full w-full"
                  retry={handleRefetch}
                />
              </div>
            )}
          </>
        )}
        {isTyping.recipient && <IsTypingBubble />}
        {/* bottom anchor */}
        <div ref={bottomRef} className="h-4 mb-1" data-id={bottomAreaId} />
      </IonContent>
      <IonFooter className="shadow-none relative">
        {/* DOWN BUTTON  */}
        {showScrollerButton && (
          <button
            type="button"
            onClick={handleScrollToBottom}
            className="absolute button-with-icon bottom-full left-1/2 -translate-x-1/2 opacity-60 bg-customs-lavender-foreground shadow-sm shadow-customs-lavender-foreground border border-gray-100 p-2 gap-0! rounded-full"
          >
            <IonIcon icon={chevronDownOutline} className="text-primary" />
          </button>
        )}
        <IonToolbar
          style={{
            '--background': 'transparent',
            '--border-width': '0',
            backdropFilter: 'none',
          }}
        >
          <div className="pt-4">
            <ChatInput
              onSend={handleMassageSending}
              onChange={handleUserTyping}
            />
          </div>
        </IonToolbar>
      </IonFooter>
    </IonPage>
  );
}
